import { useAppStore } from "@/store";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { MDSToggle } from "@/mds/components/forms";
import { BoldedList } from "@/domains/templates/generateTemplateSharingText";
import { MDSBanner, MDSBannerKind, MDSBannerSlotName, MDSText, MDSTextWeight } from "@/mds";
import { MdsIconKind } from "@/design-system/components/icon/types";
import { MdsShortcutKeyCharacter } from "@/design-system/components/shortcut-key/types";
import { MdsShortcutKey } from "@/design-system/components/shortcut-key/MdsShortcutKey";

interface TemplatesEditorBannerProps {
  template: TemplateObservable;
}

export const TemplatesEditorBanner = observer<TemplatesEditorBannerProps>(
  function TemplatesEditorBanner({ template }) {
    const { store } = useAppStore();
    const targetScopes = template.modelData.target_scopes;

    const targetScopeCollectionIds = useMemo(
      () =>
        targetScopes
          .filter(scope => scope.scope_kind === "COLLECTION_SCOPE")
          .map(scope => scope.value.collection_id),
      [targetScopes]
    );
    const targetScopeSpaceAccountIds = useMemo(
      () =>
        targetScopes
          .filter(scope => scope.scope_kind === "SPACE_ACCOUNT_SCOPE")
          .map(scope => scope.value.space_account_id)
          .filter(id => id !== store.spaceAccounts.myPersonalSpaceAccountId),
      [targetScopes, store.spaceAccounts.myPersonalSpaceAccountId]
    );

    const targetScopeCollections = useMemo(
      () => store.collections.getMany(targetScopeCollectionIds),
      [store.collections, targetScopeCollectionIds]
    );
    const targetScopeContacts = useMemo(
      () => store.contacts.getManyBySpaceAccountId(targetScopeSpaceAccountIds),
      [store.contacts, targetScopeSpaceAccountIds]
    );
    const hasTargetScopes = targetScopeCollections.length > 0 || targetScopeContacts.length > 0;
    const isAutoSharingEnabled = template.isAutoSharingEnabled;

    const { privateCollectionNames, sharedNames } = useMemo(() => {
      const privateCollectionNames = targetScopeCollections
        .filter(collection => collection.modelData.shared_at === null)
        .map(collection => collection.label);
      const sharedCollectionNames = targetScopeCollections
        .filter(collection => collection.modelData.shared_at !== null)
        .map(collection => collection.label);
      const contactEmails = targetScopeContacts.map(contact => contact?.profileEmailAddress);
      const sharedNames = [...sharedCollectionNames, ...contactEmails];
      return { privateCollectionNames, sharedNames };
    }, [targetScopeCollections, targetScopeContacts]);

    const headingText = useMemo(() => {
      if (privateCollectionNames.length > 0 && sharedNames.length > 0) {
        return (
          <>
            Automatically add notes created with this template to{" "}
            <BoldedList items={privateCollectionNames} /> and share notes created with this template
            with <BoldedList items={sharedNames} />
          </>
        );
      }
      if (privateCollectionNames.length > 0) {
        return (
          <>
            Automatically add notes created with this template to{" "}
            <BoldedList items={privateCollectionNames} />
          </>
        );
      }
      if (sharedNames.length > 0) {
        return (
          <>
            Automatically share notes created with this template with{" "}
            <BoldedList items={sharedNames} />
          </>
        );
      }
      return (
        <>
          You&apos;re editing a template. Type{" "}
          <MdsShortcutKey character={MdsShortcutKeyCharacter.Backslash} inline /> to insert date and
          name variables.
        </>
      );
    }, [privateCollectionNames, sharedNames]);

    const handleAutoSharingSwitch = async (value: boolean) => {
      await template.update({ isAutoSharingEnabled: value });
    };

    return (
      <MDSBanner
        kind={hasTargetScopes && isAutoSharingEnabled ? MDSBannerKind.Info : MDSBannerKind.Default}
        iconKind={MdsIconKind.InfoCircle}
        message={headingText}
      >
        {hasTargetScopes && (
          <ToggleContainer slot={MDSBannerSlotName.Right}>
            <MDSText weight={MDSTextWeight.Smedium}>{isAutoSharingEnabled ? "On" : "Off"}</MDSText>
            <MDSToggle
              checked={isAutoSharingEnabled}
              onChange={() => handleAutoSharingSwitch(!isAutoSharingEnabled)}
            />
          </ToggleContainer>
        )}
      </MDSBanner>
    );
  }
);

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;
